import type { UnknownAction } from '@reduxjs/toolkit'

import {
  defineListener,
  errorView,
  resourceListTag,
  resourceTag,
  type ModuleListener,
  type ModuleListenerApi,
} from '@krp/client-runtime'

import { AQSI_TAGS } from '../../../shared/api/tags'
import { storeDispatch, type AqsiDispatch } from '../../../shared/model/dispatch'
import { feedbackOfError, type CommandFeedback } from '../../../shared/model/feedback'
import { FEEDBACK_SCOPES, feedbackSet, feedbackSendingFinished } from '../../../shared/model/feedbackSlice'
import { newCommandIntent } from '../../../shared/model/ids'
import { ridKey } from '../../../shared/model/keys'
import { saleSeatCleared } from '../../sale/model/saleSlice'
import {
  aqsiOperation,
  aqsiMySession,
  aqsiRegisterLinkCheck,
  aqsiRegisters,
  aqsiShift,
  aqsiOperationReconcile,
  aqsiSessionClose,
  aqsiSessionCloseForeign,
  aqsiSessionHeartbeat,
  aqsiSessionOpen,
  aqsiSessionPriceType,
  aqsiSessionTakeover,
  aqsiShiftAdopt,
  aqsiShiftCommand,
  aqsiShiftOperationResolve,
  rmkApi,
} from '../api/rmkApi'
import {
  linkCheckRequested,
  operationCheckRequested,
  operationReconcileRequested,
  registerWorkRequested,
  rmkReloadRequested,
  sessionCloseForeignRequested,
  sessionCloseRequested,
  sessionHeartbeatRequested,
  sessionPriceTypeRequested,
  sessionTakeoverRequested,
  shiftAdoptRequested,
  shiftCommandRequested,
  shiftOperationResolveRequested,
} from './commands'
import {
  liveProofOf,
  proofDropped,
  type CashierAccessState,
} from './cashierAccessSlice'
import { openingFinished, openingPhaseChanged, openingStarted } from './openingSlice'
import { probeRegister } from './probe'
import { aqsiRegisterHealth, healthCheckText } from './registerHealth'
import { startWorkRemembered } from './startSlice'
import {
  blocksShiftCommands,
  operationAdopted,
  operationAccepted,
  operationNotFound,
  operationRejected,
  operationSettled,
  operationStarted,
  operationUnknown,
  pendingOfRegister,
} from './operationsSlice'
import type { AqsiOperationStatus, AqsiRegisters, AqsiShift, OperationsIntentState } from './types'

/** Итог команды, который знает служба: дальше ждать нечего. */
export function isSettledState(state: AqsiOperationStatus['state']): boolean {
  return state === 'Succeeded' || state === 'Failed'
}

const rmkKey = (resourceId: string) => ridKey(resourceId, FEEDBACK_SCOPES.rmk)

/**
 * Сколько ждать, прежде чем 404 статуса значит «команда до службы не дошла».
 * Служба заводит запись операции только после чтения факта смены у кабинета, а
 * клиент ждёт ответа на команду до 60 с: раньше этого 404 значит «ещё не записана».
 */
export const NOT_FOUND_AFTER_MS = 3 * 60_000

/** 404 статуса — именно «операции нет в журнале ресурса» (а не маршрут Gateway) и окно отправки прошло. */
function provesNeverArrived(error: unknown, startedAt: string, now: number): boolean {
  const view = errorView(error)
  if (view.kind !== 'not-found' || view.code !== 'operation-not-found') return false
  const started = Date.parse(startedAt)
  return Number.isFinite(started) && now - started >= NOT_FOUND_AFTER_MS
}

const UUID_IN_TEXT = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i

/**
 * Отказ 409 `operation-unresolved`: служба называет незавершённую команду смены на
 * кассе. Поля с её id в договоре C3a нет — id берётся из текста отказа (запрос к C:
 * отдавать полем). Без этого окно, потерявшее хранилище, не могло бы ни прочитать
 * статус, ни сверить команду.
 */
function adoptUnresolved(dispatch: AqsiDispatch, resourceId: string, registerKey: string, error: unknown): void {
  const view = errorView(error)
  if (view.code !== 'operation-unresolved') return
  const operationId = UUID_IN_TEXT.exec(view.message)?.[0]
  if (operationId === undefined) return
  dispatch(
    operationAdopted({
      resourceId,
      registerKey,
      operationId: operationId.toLowerCase(),
      startedAt: new Date(0).toISOString(),
    }),
  )
}

function feedback(
  command: string,
  target: string,
  phase: CommandFeedback['phase'],
  note: string | null,
): CommandFeedback {
  return { command, target, phase, error: null, note }
}

/** Что перечитать, когда смена могла измениться. */
function invalidateSeat(dispatch: AqsiDispatch, resourceId: string, deviceId: number): void {
  dispatch(
    rmkApi.util.invalidateTags([
      resourceListTag(AQSI_TAGS.registers, resourceId),
      resourceTag(AQSI_TAGS.shift, resourceId, String(deviceId)),
      resourceListTag(AQSI_TAGS.saleActive, resourceId),
      // Смена — часть проверки корзины («смена закрыта»): после её изменения проверка читается заново.
      resourceListTag(AQSI_TAGS.saleCheck, resourceId),
    ]),
  )
}

/**
 * Команда над сессией: отправить ОДИН раз и показать итог. Без ответа
 * (`unknown-outcome`) — не повторять: endpoint уже перечитывает кассы
 * (invalidatesTags), экран показывает, чья касса на самом деле.
 */
async function runSessionCommand(
  dispatch: AqsiDispatch,
  resourceId: string,
  command: string,
  target: string,
  send: () => Promise<unknown>,
  done: string,
  seatKey?: string,
): Promise<boolean> {
  const key = rmkKey(resourceId)
  dispatch(feedbackSet({ key, feedback: feedback(command, target, 'sending', null) }))
  try {
    await send()
    dispatch(feedbackSet({ key, feedback: feedback(command, target, 'done', done) }))
    return true
  } catch (error) {
    const result = feedbackOfError(command, target, error)
    if (seatKey !== undefined) adoptUnresolved(dispatch, resourceId, seatKey, error)
    dispatch(
      feedbackSet({
        key,
        feedback:
          result.phase === 'unknown'
            ? {
                ...result,
                note: 'Ответ не получен. Команда не повторяется — состояние касс перечитано, смотрите ниже.',
              }
            : result,
      }),
    )
    return false
  }
}

/**
 * Живой факт смены расходится с тем, что держит список касс: строка этой кассы или «Ваша работа» (смена
 * своей сессии). Списка ещё нет или кассы в нём нет — расходиться нечему.
 */
export function shiftContradictsRegisters(
  registers: AqsiRegisters | undefined,
  deviceId: number,
  shift: AqsiShift,
): boolean {
  const register = registers?.registers.find((item) => item.deviceId === deviceId)
  if (register === undefined) return false
  if (register.shiftOpen !== shift.isOpen) return true
  if (shift.isOpen && register.shiftNumber !== null && register.shiftNumber !== shift.number) return true
  const mine = registers?.mine
  if (mine === null || mine === undefined || mine.deviceId !== deviceId || mine.shiftNumber === null) {
    return false
  }
  return !(shift.isOpen && shift.number === mine.shiftNumber)
}

export function createRmkListeners<S>(
  selectOperations: (state: S) => OperationsIntentState,
  selectAccess: (state: S) => CashierAccessState,
): ModuleListener<S>[] {
  /** Работа за кассой одна на человека в источнике: второе нажатие «Открыть смену», пока идёт первое, не рождает команды. */
  const startingSources = new Set<string>()
  const closingSessions = new Set<string>()
  const closingSessionReads = new Set<string>()
  const reconcilingOperations = new Set<string>()
  const sendingShiftCommands = new Set<string>()
  const closeOwnSession = async (dispatch: AqsiDispatch, resourceId: string, registerKey: string) => {
    const key = ridKey(resourceId, registerKey)
    if (closingSessionReads.has(key)) return
    closingSessionReads.add(key)
    try {
      const request = dispatch(aqsiMySession.initiate({ resourceId }, { forceRefetch: true }))
      const session = await request.unwrap().finally(() => request.unsubscribe())
      if (session === null || session.registerKey !== registerKey || session.shiftNumber !== null || closingSessions.has(session.id)) return
      closingSessions.add(session.id)
      try {
        await runSessionCommand(
          dispatch,
          resourceId,
          'session.close',
          String(session.deviceId),
          () => dispatch(aqsiSessionClose.initiate({
            resourceId,
            deviceId: session.deviceId,
            sessionId: session.id,
            expectedVersion: session.version,
            intent: newCommandIntent(),
          }, { track: false })).unwrap(),
          'Смена закрыта. Рабочее место освобождено',
        )
      } finally {
        closingSessions.delete(session.id)
      }
    } catch (error) {
      dispatch(feedbackSet({ key: rmkKey(resourceId), feedback: feedbackOfError('session.close', registerKey, error) }))
    } finally {
      closingSessionReads.delete(key)
    }
  }
  const settle = (
    dispatch: AqsiDispatch,
    ref: { resourceId: string; registerKey: string; deviceId?: number },
    status: AqsiOperationStatus,
  ) => {
    if (!isSettledState(status.state)) return
    if (status.state === 'Succeeded') dispatch(saleSeatCleared({ resourceId: ref.resourceId, registerKey: ref.registerKey }))
    dispatch(operationSettled({ resourceId: ref.resourceId, registerKey: ref.registerKey, operationId: status.operationId }))
    invalidateSeat(dispatch, ref.resourceId, ref.deviceId ?? status.deviceId)
    if (status.state === 'Succeeded' && status.kind === 'aqsi.shift.close') {
      dispatch(feedbackSet({ key: rmkKey(ref.resourceId), feedback: feedback('shift.close', ref.registerKey, 'sending', 'Смена закрыта. Освобождаем рабочее место…') }))
      void closeOwnSession(dispatch, ref.resourceId, ref.registerKey)
    } else {
      dispatch(feedbackSet({ key: rmkKey(ref.resourceId), feedback: feedback(`shift.${status.kind === 'aqsi.shift.open' ? 'open' : 'close'}`, ref.registerKey, status.state === 'Succeeded' ? 'done' : 'rejected', status.state === 'Succeeded' ? 'Смена открыта' : status.note ?? 'Команда смены не выполнена') }))
    }
  }

  /** Открыть/закрыть смену: один POST, неизвестный исход прошлой команды запрещает новую (ADR-11). */
  const runShiftCommand = async (
    dispatch: AqsiDispatch,
    state: OperationsIntentState,
    payload: {
      resourceId: string
      deviceId: number
      registerKey: string
      sessionId: string
      expectedVersion: number
      kind: 'open' | 'close'
    },
  ): Promise<boolean> => {
    const { resourceId, deviceId, registerKey, sessionId, expectedVersion, kind } = payload
    const command = `shift.${kind}`
    if (blocksShiftCommands(pendingOfRegister(state, resourceId, registerKey))) {
      dispatch(
        feedbackSet({
          key: rmkKey(resourceId),
          feedback: feedback(
            command,
            registerKey,
            'rejected',
            'Прошлая команда смены ещё не завершена или её исход неизвестен. Проверьте статус — повторная команда запрещена.',
          ),
        }),
      )
      return false
    }
    const intent = newCommandIntent()
    const ref = { resourceId, registerKey, operationId: intent.operationId }
    dispatch(operationStarted({ ...ref, kind, startedAt: new Date().toISOString() }))
    dispatch(feedbackSet({ key: rmkKey(resourceId), feedback: feedback(command, registerKey, 'sending', kind === 'close' ? 'Закрываем смену на кассе…' : 'Открываем смену на кассе…') }))
    try {
      const status = await dispatch(
        aqsiShiftCommand.initiate({ resourceId, deviceId, sessionId, expectedVersion, kind, intent }, { track: false }),
      ).unwrap()
      void dispatch(rmkApi.util.upsertQueryData('aqsiOperation', { resourceId, operationId: status.operationId }, status))
      dispatch(operationAccepted(ref))
      dispatch(feedbackSendingFinished({ key: rmkKey(resourceId), command, target: registerKey }))
      settle(dispatch, { resourceId, registerKey, deviceId }, status)
      return true
    } catch (error) {
      const result = feedbackOfError(command, registerKey, error)
      if (result.phase === 'unknown') {
        dispatch(operationUnknown(ref))
        dispatch(feedbackSet({ key: rmkKey(resourceId), feedback: { ...result, note: 'Ответ не получен. Команда не повторяется; проверяем её статус.' } }))
      } else {
        dispatch(operationRejected(ref))
        adoptUnresolved(dispatch, resourceId, registerKey, error)
        dispatch(feedbackSet({ key: rmkKey(resourceId), feedback: result }))
      }
      return false
    }
  }

  return [
    // «Проверить кассу»: один POST; итог — слова двух уровней связи, как в мини-окне точки «Связь». Без ответа — не повторять: связь перечитывается.
    defineListener<S, ReturnType<typeof linkCheckRequested>>(linkCheckRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const { resourceId, deviceId } = action.payload
      const key = rmkKey(resourceId)
      const target = String(deviceId)
      dispatch(feedbackSet({ key, feedback: feedback('link.check', target, 'sending', 'Проверяем, отвечает ли касса…') }))
      try {
        const link = await dispatch(aqsiRegisterLinkCheck.initiate({ resourceId, deviceId }, { track: false })).unwrap()
        const text = healthCheckText(aqsiRegisterHealth(link, { deviceId, name: null }))
        dispatch(feedbackSet({ key, feedback: feedback('link.check', target, 'done', text) }))
      } catch (error) {
        dispatch(feedbackSet({ key, feedback: feedbackOfError('link.check', target, error) }))
      }
    }),

    // «Открыть смену» / «Продолжить смену» ОДНИМ действием (RP-FB22 3): проверка связи → работа за кассой с кассиром,
    // видом цены и подтверждением кода → смена. До этого нажатия на сервере ничего не создаётся.
    defineListener<S, ReturnType<typeof registerWorkRequested>>(registerWorkRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const { resourceId, deviceId, mode, cashierId, cashierName, cashierNeedsCode, asSelf, priceType, registerName, priceTypeId } =
        action.payload
      const key = rmkKey(resourceId)
      const target = String(deviceId)
      const command = mode === 'open' ? 'register.open-shift' : 'register.continue-shift'
      const sourceKey = resourceId.toLowerCase()
      if (startingSources.has(sourceKey)) return
      // Подтверждение кода — из состояния, а не из действия: истекло к нажатию — не отправляем ничего и просим код ещё раз.
      let cashierProof: string | undefined
      if (cashierId !== undefined && cashierNeedsCode === true) {
        const record = liveProofOf(selectAccess(api.getModuleState()), resourceId, cashierId, Date.now())
        if (record === undefined) {
          const message = 'Подтверждение кода истекло — подтвердите код ещё раз. Ваш выбор сохранён.'
          dispatch(proofDropped({ resourceId, cashierId, message }))
          dispatch(feedbackSet({ key, feedback: feedback(command, target, 'rejected', message) }))
          return
        }
        cashierProof = record.proof
      }
      startingSources.add(sourceKey)
      dispatch(
        openingStarted({
          resourceId,
          deviceId,
          mode,
          registerName: registerName ?? 'Касса',
          cashierName: cashierName ?? null,
          startedAt: new Date().toISOString(),
        }),
      )
      try {
        dispatch(feedbackSet({ key, feedback: feedback(command, target, 'sending', 'Начинаем работу за кассой…') }))
        const gate = await probeRegister(dispatch, resourceId, deviceId, 'shift')
        if (!gate.proceed) {
          dispatch(feedbackSet({ key, feedback: feedback(command, target, 'rejected', gate.message) }))
          return
        }
        dispatch(openingPhaseChanged({ resourceId, phase: 'send' }))
        let session
        try {
          session = await dispatch(
            aqsiSessionOpen.initiate(
              {
                resourceId,
                deviceId,
                intent: newCommandIntent(),
                ...(cashierId !== undefined ? { cashierId } : {}),
                ...(cashierProof !== undefined ? { cashierProof } : {}),
                ...(asSelf === true && cashierId === undefined ? { asSelf: true } : {}),
                ...(priceType !== undefined ? { priceType } : {}),
              },
              { track: false },
            ),
          ).unwrap()
        } catch (error) {
          const result = feedbackOfError(command, target, error)
          // Служба не приняла подтверждение (чужое, просроченное, не то): оно больше не годится, код вводится заново.
          if (cashierId !== undefined && cashierProof !== undefined && errorView(error).kind === 'forbidden') {
            dispatch(proofDropped({ resourceId, cashierId, message: 'Служба не приняла подтверждение кода — подтвердите код ещё раз.' }))
          }
          dispatch(
            feedbackSet({
              key,
              feedback:
                result.phase === 'unknown'
                  ? { ...result, note: 'Ответ не получен. Команда не повторяется — состояние касс перечитано, смотрите ниже.' }
                  : result,
            }),
          )
          return
        }
        // Работа начата: этот выбор стал «как в прошлый раз» на этом устройстве, черновик пуст.
        if (priceType !== undefined) {
          dispatch(
            startWorkRemembered({
              resourceId,
              cashier: cashierId === undefined ? { kind: 'own' } : { kind: 'cashier', id: cashierId, name: cashierName ?? '' },
              deviceId,
              priceTypeId: priceTypeId ?? (priceType === null ? 'cash' : `${priceType.resourceId.toLowerCase()}/${priceType.key}`),
            }),
          )
        }
        const own = { resourceId, deviceId, registerKey: session.registerKey, sessionId: session.id, expectedVersion: session.version }
        if (mode === 'continue') {
          await runSessionCommand(
            dispatch,
            resourceId,
            command,
            target,
            () => dispatch(aqsiShiftAdopt.initiate({ ...own, intent: newCommandIntent() }, { track: false })).unwrap(),
            'Смена принята',
            session.registerKey,
          )
          return
        }
        await runShiftCommand(dispatch, selectOperations(api.getModuleState()), { ...own, kind: 'open' })
      } finally {
        startingSources.delete(sourceKey)
        dispatch(openingFinished({ resourceId }))
      }
    }),

    defineListener<S, ReturnType<typeof sessionCloseRequested>>(sessionCloseRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const args = { ...action.payload, intent: newCommandIntent() }
      await runSessionCommand(
        dispatch,
        args.resourceId,
        'session.close',
        String(args.deviceId),
        () => dispatch(aqsiSessionClose.initiate(args, { track: false })).unwrap(),
        'Работа за кассой закончена. Смена кассы не закрывалась',
      )
    }),

    // RP-FB11: вид цены своей работы. Между чеками: посреди чека служба откажет словами («sale-active»).
    defineListener<S, ReturnType<typeof sessionPriceTypeRequested>>(sessionPriceTypeRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const { name, ...rest } = action.payload
      const args = { ...rest, intent: newCommandIntent() }
      await runSessionCommand(
        dispatch,
        args.resourceId,
        'session.price-type',
        String(args.deviceId),
        () => dispatch(aqsiSessionPriceType.initiate(args, { track: false })).unwrap(),
        `Вид цены: ${name}`,
      )
    }),

    defineListener<S, ReturnType<typeof sessionTakeoverRequested>>(
      sessionTakeoverRequested.match,
      async (action, api) => {
        const dispatch = storeDispatch(api.dispatch)
        const args = { ...action.payload, intent: newCommandIntent() }
        await runSessionCommand(
          dispatch,
          args.resourceId,
          'session.takeover',
          String(args.deviceId),
          () => dispatch(aqsiSessionTakeover.initiate(args, { track: false })).unwrap(),
          'Касса передана вам',
        )
      },
    ),

    defineListener<S, ReturnType<typeof sessionCloseForeignRequested>>(
      sessionCloseForeignRequested.match,
      async (action, api) => {
        const dispatch = storeDispatch(api.dispatch)
        const args = { ...action.payload, intent: newCommandIntent() }
        await runSessionCommand(
          dispatch,
          args.resourceId,
          'session.close-foreign',
          String(args.deviceId),
          () => dispatch(aqsiSessionCloseForeign.initiate(args, { track: false })).unwrap(),
          'Чужая работа за кассой закрыта',
        )
      },
    ),

    defineListener<S, ReturnType<typeof shiftAdoptRequested>>(shiftAdoptRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const args = { ...action.payload, intent: newCommandIntent() }
      await runSessionCommand(
        dispatch,
        args.resourceId,
        'shift.adopt',
        String(args.deviceId),
        () => dispatch(aqsiShiftAdopt.initiate(args, { track: false })).unwrap(),
        'Смена принята',
        args.registerKey,
      )
    }),

    // Пульс: молча. Отказ (сессию передали/закрыли) — показать: касса уже не ваша.
    defineListener<S, ReturnType<typeof sessionHeartbeatRequested>>(
      sessionHeartbeatRequested.match,
      async (action, api) => {
        const dispatch = storeDispatch(api.dispatch)
        const args = { ...action.payload, intent: newCommandIntent() }
        try {
          await dispatch(aqsiSessionHeartbeat.initiate(args, { track: false })).unwrap()
        } catch (error) {
          const view = errorView(error)
          // Рабочее место перенесли в другую вкладку (RP-CA5): это не сбой связи. Экран «открыто в другой вкладке» покажет перечитанная
          // работа, внизу ничего не всплывает.
          if (view.code === 'session-other-tab') {
            dispatch(rmkApi.util.invalidateTags([resourceListTag(AQSI_TAGS.registers, args.resourceId)]))
            return
          }
          if (view.kind === 'not-found' || view.kind === 'forbidden' || view.kind === 'conflict') {
            dispatch(
              feedbackSet({
                key: rmkKey(args.resourceId),
                feedback: {
                  ...feedbackOfError('session.heartbeat', String(args.deviceId), error),
                  note: 'Связь с вашей работой за кассой потеряна',
                },
              }),
            )
          }
        }
      },
    ),

    // «Открыть смену» на пороге: касса получит команду — сначала проверка связи (если включена и не «на связи»).
    defineListener<S, ReturnType<typeof shiftCommandRequested>>(shiftCommandRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const { resourceId, deviceId, registerKey, kind } = action.payload
      const sendingKey = ridKey(resourceId, registerKey)
      // До ответа на первое нажатие повторное молча присоединяется к ожиданию: не отправляет POST и не затирает feedback.
      if (sendingShiftCommands.has(sendingKey)) return
      sendingShiftCommands.add(sendingKey)
      try {
        if (kind === 'open') {
          const key = rmkKey(resourceId)
          dispatch(feedbackSet({ key, feedback: feedback('shift.open', registerKey, 'sending', 'Проверяем, отвечает ли касса…') }))
          const gate = await probeRegister(dispatch, resourceId, deviceId, 'shift')
          if (!gate.proceed) {
            dispatch(feedbackSet({ key, feedback: feedback('shift.open', registerKey, 'rejected', gate.message) }))
            return
          }
        }
        await runShiftCommand(dispatch, selectOperations(api.getModuleState()), action.payload)
      } finally {
        sendingShiftCommands.delete(sendingKey)
      }
    }),

    defineListener<S, ReturnType<typeof operationCheckRequested>>(
      operationCheckRequested.match,
      async (action, api) => {
        const dispatch = storeDispatch(api.dispatch)
        const { resourceId, registerKey, operationId } = action.payload
        const result = await dispatch(
          aqsiOperation.initiate({ resourceId, operationId }, { subscribe: false, forceRefetch: true }),
        )
        if (result.error !== undefined) {
          const pending = pendingOfRegister(selectOperations(api.getModuleState()), resourceId, registerKey)
          if (pending?.operationId === operationId && provesNeverArrived(result.error, pending.startedAt, Date.now())) {
            dispatch(operationNotFound({ resourceId, registerKey, operationId }))
          }
          return
        }
        if (result.data !== undefined) {
          dispatch(operationAccepted({ resourceId, registerKey, operationId }))
          settle(dispatch, { resourceId, registerKey }, result.data)
        }
      },
    ),

    defineListener<S, ReturnType<typeof operationReconcileRequested>>(
      operationReconcileRequested.match,
      async (action, api) => {
        const dispatch = storeDispatch(api.dispatch)
        const { resourceId, registerKey, operationId, expectedVersion } = action.payload
        if (reconcilingOperations.has(operationId)) return
        reconcilingOperations.add(operationId)
        const key = rmkKey(resourceId)
        dispatch(feedbackSet({ key, feedback: feedback('operation.reconcile', registerKey, 'sending', 'Проверяем результат на кассе…') }))
        try {
          const status = await dispatch(
            aqsiOperationReconcile.initiate(
              { resourceId, operationId, expectedVersion, intent: newCommandIntent() },
              { track: false },
            ),
          ).unwrap()
          void dispatch(rmkApi.util.upsertQueryData('aqsiOperation', { resourceId, operationId }, status))
          dispatch(feedbackSendingFinished({ key, command: 'operation.reconcile', target: registerKey }))
          settle(dispatch, { resourceId, registerKey }, status)
        } catch (error) {
          dispatch(feedbackSet({ key, feedback: feedbackOfError('operation.reconcile', registerKey, error) }))
        } finally {
          reconcilingOperations.delete(operationId)
        }
      },
    ),

    // UI-ACTIONS P1 №5: ручная сверка команды смены — служба принимает её, кабинет не вызывается; повтора нет.
    defineListener<S, ReturnType<typeof shiftOperationResolveRequested>>(
      shiftOperationResolveRequested.match,
      async (action, api) => {
        const dispatch = storeDispatch(api.dispatch)
        const { resourceId, registerKey, operationId, expectedVersion, outcome, reason, checked } = action.payload
        if (reason.trim() === '' || checked.trim() === '') return
        const key = rmkKey(resourceId)
        dispatch(feedbackSet({ key, feedback: feedback('operation.resolve', registerKey, 'sending', null) }))
        try {
          const status = await dispatch(
            aqsiShiftOperationResolve.initiate(
              { resourceId, operationId, expectedVersion, intent: newCommandIntent(), outcome, reason: reason.trim(), checked: checked.trim() },
              { track: false },
            ),
          ).unwrap()
          void dispatch(rmkApi.util.upsertQueryData('aqsiOperation', { resourceId, operationId }, status))
          settle(dispatch, { resourceId, registerKey }, status)
          if (status.kind !== 'aqsi.shift.close' || status.state !== 'Succeeded')
            dispatch(feedbackSet({ key, feedback: feedback('operation.resolve', registerKey, 'done', 'Ручная сверка записана') }))
        } catch (error) {
          const result = feedbackOfError('operation.resolve', registerKey, error)
          dispatch(
            feedbackSet({
              key,
              feedback:
                result.phase === 'unknown'
                  ? { ...result, note: 'Ответ не получен. Сверка не повторяется — проверьте статус команды.' }
                  : result,
            }),
          )
        }
      },
    ),

    // Чтение смены у кабинета — живой факт, и служба по нему ПЕРЕПИСЫВАЕТ своё наблюдение и сессию (смена закрыта
    // на кассе вне программы). Список касс прочитан раньше и держит старое «открыта»: перечитать его, иначе строка
    // кассы и «Ваша работа» в одном окне говорят разное (жалоба владельца 08.10.2026, UX-SRC п. 4). Только при
    // расхождении — лишнего чтения касс на каждую проверку смены нет. Смену не трогаем — цикла нет.
    {
      predicate: (action: UnknownAction) => aqsiShift.matchFulfilled(action),
      effect: (action: UnknownAction, api: ModuleListenerApi<S>) => {
        if (!aqsiShift.matchFulfilled(action)) return
        const { resourceId, deviceId } = action.meta.arg.originalArgs
        const dispatch = storeDispatch(api.dispatch)
        const selectRegisters = aqsiRegisters.select({ resourceId })
        const registers = dispatch(
          (_, getState) => selectRegisters(getState() as Parameters<typeof selectRegisters>[0]).data,
        )
        if (!shiftContradictsRegisters(registers, deviceId, action.payload)) return
        if (!action.payload.isOpen && registers?.mine?.deviceId === deviceId)
          void closeOwnSession(dispatch, resourceId, registers.mine.registerKey)
        if (!action.payload.isOpen && registers?.mine?.deviceId === deviceId)
          dispatch(saleSeatCleared({ resourceId, registerKey: registers.mine.registerKey }))
        dispatch(rmkApi.util.invalidateTags([
          resourceListTag(AQSI_TAGS.registers, resourceId),
          resourceListTag(AQSI_TAGS.saleActive, resourceId),
        ]))
      },
    },

    // Опрос статуса (Pending/Sent) мог увидеть итог сам — отметить и перечитать кассу.
    {
      predicate: (action: UnknownAction) => aqsiOperation.matchFulfilled(action),
      effect: (action: UnknownAction, api: ModuleListenerApi<S>) => {
        if (!aqsiOperation.matchFulfilled(action)) return
        const status = action.payload
        const { resourceId } = action.meta.arg.originalArgs
        const pending = pendingOfRegister(selectOperations(api.getModuleState()), resourceId, status.registerKey)
        if (pending?.operationId !== status.operationId || pending.phase === 'settled') return
        settle(storeDispatch(api.dispatch), { resourceId, registerKey: status.registerKey }, status)
      },
    },

    // Чтение статуса после F5 или обрыва: служба команды не знает — до неё она не дошла.
    {
      predicate: (action: UnknownAction) => aqsiOperation.matchRejected(action),
      effect: (action: UnknownAction, api: ModuleListenerApi<S>) => {
        if (!aqsiOperation.matchRejected(action)) return
        const { resourceId, operationId } = action.meta.arg.originalArgs
        const entry = Object.values(selectOperations(api.getModuleState()).pending).find(
          (item) => item.operationId === operationId && item.phase === 'unknown',
        )
        if (entry !== undefined && provesNeverArrived(action.payload, entry.startedAt, Date.now())) {
          storeDispatch(api.dispatch)(operationNotFound({ resourceId, registerKey: entry.registerKey, operationId }))
        }
      },
    },

    defineListener<S, ReturnType<typeof rmkReloadRequested>>(rmkReloadRequested.match, (action, api) => {
      const { resourceId, deviceId } = action.payload
      const dispatch = storeDispatch(api.dispatch)
      dispatch(
        rmkApi.util.invalidateTags([
          resourceListTag(AQSI_TAGS.registers, resourceId),
          ...(deviceId !== null
            ? [
                resourceTag(AQSI_TAGS.shift, resourceId, String(deviceId)),
                resourceTag(AQSI_TAGS.journal, resourceId, String(deviceId)),
                resourceTag(AQSI_TAGS.link, resourceId, String(deviceId)),
              ]
            : []),
        ]),
      )
    }),
  ]
}
